import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FileText, UploadSimple, Trash, Sparkle, CaretDown, Scales, FilePdf, Lightning, X, DownloadSimple, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITR_TYPES = ["ITR-1", "ITR-2", "ITR-3", "ITR-4", "ITR-5", "ITR-6", "ITR-7"];
const AY = ["2026-27", "2025-26", "2024-25"];

/* ---------- schedule field configs (integrated ITR-1..7) ---------- */
const SCHEDULES = {
  salary: {
    title: "Schedule S — Salary", itr: "ITR-1,2,3,4",
    fields: [
      ["sal_171", "Salary u/s 17(1) — Basic/Gross"], ["sal_172", "Perquisites u/s 17(2)"],
      ["sal_173", "Profits in lieu u/s 17(3)"], ["sal_exempt", "Exempt Allowances (HRA/LTA u/s 10)"],
      ["sal_prof_tax", "Professional Tax u/s 16(iii)"], ["sal_ent_allow", "Entertainment Allowance 16(ii)"],
    ],
    text: [["employer_name", "Employer Name"], ["employer_tan", "Employer TAN"]],
  },
  hp: {
    title: "Schedule HP — House Property", itr: "ITR-1,2,3",
    fields: [
      ["hp_count", "No. of House Properties", true], ["hp_gross_rent", "Gross Rent Received (Let-out)"],
      ["hp_municipal", "Municipal Taxes Paid"], ["hp_interest", "Interest on Housing Loan u/s 24(b)"],
      ["hp_arrears", "Arrears/Unrealised Rent (70%)"],
    ],
    select: [["hp_type", "Property Type", ["self_occupied", "let_out", "deemed_let_out"]]],
  },
  pgbp: {
    title: "Schedule BP — Business / Profession (PGBP)", itr: "ITR-3,4,5,6",
    fields: [
      ["biz_turnover", "Gross Turnover / Receipts"], ["biz_gross_profit", "Gross Profit"],
      ["biz_net_profit", "Net Profit (Regular Books)"], ["biz_44ad", "Presumptive Income 44AD (Business)"],
      ["biz_44ada", "Presumptive Income 44ADA (Profession)"], ["biz_44ae", "Presumptive Income 44AE (Goods Carriage)"],
      ["biz_depreciation", "Depreciation Claimed"], ["biz_partner_remuneration", "Remuneration from Firm (Partner)"],
    ],
    text: [["biz_nature", "Nature of Business / Profession Code"]],
    checks: [["biz_books", "Books of account maintained"], ["biz_audit", "Tax Audit u/s 44AB applicable"]],
  },
  cg: {
    title: "Schedule CG — Capital Gains", itr: "ITR-2,3,5,6",
    fields: [
      ["cg_stcg_111a", "STCG u/s 111A (Equity, 20%)"], ["cg_stcg_other", "STCG — Other Assets (slab)"],
      ["cg_ltcg_112a", "LTCG u/s 112A (Equity, 12.5% > ₹1.25L)"], ["cg_ltcg_other", "LTCG — Other Assets (12.5%)"],
      ["cg_exemption_54", "Exemptions u/s 54/54F/54EC"],
    ],
  },
  os: {
    title: "Schedule OS — Income from Other Sources", itr: "All ITRs",
    fields: [
      ["os_savings_interest", "Savings Bank Interest"], ["os_fd_interest", "FD / Term Deposit Interest"],
      ["os_dividend", "Dividend Income"], ["os_family_pension", "Family Pension"],
      ["os_lottery", "Winnings — Lottery / Online Games (30%)"], ["os_other", "Any Other Income"],
    ],
  },
  firm: {
    title: "Schedule — Firm / LLP Details (ITR-5)", itr: "ITR-5", entity: ["firm", "llp"],
    fields: [
      ["firm_partners_count", "Number of Partners", true], ["firm_partner_remuneration", "Remuneration to Partners u/s 40(b)"],
      ["firm_partner_interest", "Interest to Partners (max 12%)"],
    ],
    text: [["firm_llpin", "Firm Reg. No. / LLPIN"]],
  },
  company: {
    title: "Schedule — Company Details (ITR-6)", itr: "ITR-6", entity: ["company"],
    fields: [["co_mat_credit", "MAT Credit u/s 115JAA"], ["co_book_profit", "Book Profit u/s 115JB"]],
    text: [["co_cin", "CIN"], ["co_category", "Company Category (Domestic/Foreign)"]],
  },
  trust: {
    title: "Schedule — Trust / Institution (ITR-7)", itr: "ITR-7", entity: ["trust"],
    fields: [
      ["tr_voluntary", "Voluntary Contributions"], ["tr_corpus", "Corpus Donations"],
      ["tr_applied", "Income Applied to Objects (85%)"], ["tr_accumulated", "Income Accumulated u/s 11(2)"],
    ],
    text: [["tr_reg_12a", "Registration No. u/s 12A/12AB"], ["tr_reg_80g", "80G Approval No."], ["tr_section", "Return Section (139(4A)/(4B)/(4C)/(4D))"]],
  },
  via: {
    title: "Chapter VI-A — Deductions", itr: "Old regime (individuals)",
    fields: [
      ["d_80c", "80C — LIC/PPF/ELSS (max 1.5L)"], ["d_80ccc", "80CCC — Pension Fund"],
      ["d_80ccd1b", "80CCD(1B) — NPS (max 50k)"], ["d_80ccd2", "80CCD(2) — Employer NPS"],
      ["d_80d", "80D — Health Insurance"], ["d_80dd", "80DD — Disabled Dependent"],
      ["d_80ddb", "80DDB — Medical Treatment"], ["d_80e", "80E — Education Loan Interest"],
      ["d_80eea", "80EE/80EEA — Home Loan Interest"], ["d_80g", "80G — Donations"],
      ["d_80gg", "80GG — Rent Paid (no HRA)"], ["d_80tta", "80TTA — Savings Interest (max 10k)"],
      ["d_80ttb", "80TTB — Sr. Citizen Interest (max 50k)"], ["d_80u", "80U — Self Disability"],
      ["d_other", "Other Deductions"],
    ],
  },
  taxpaid: {
    title: "Taxes Paid & Advance Tax", itr: "All ITRs",
    fields: [
      ["tp_tds_salary", "TDS on Salary (Form 16)"], ["tp_tds_other", "TDS — Others (26AS/AIS)"],
      ["tp_tcs", "TCS Collected"], ["tp_adv_q1", "Advance Tax — Q1 (by 15 Jun)"],
      ["tp_adv_q2", "Advance Tax — Q2 (by 15 Sep)"], ["tp_adv_q3", "Advance Tax — Q3 (by 15 Dec)"],
      ["tp_adv_q4", "Advance Tax — Q4 (by 15 Mar)"], ["tp_self_assessment", "Self-Assessment Tax"],
    ],
  },
};

const n = (v) => parseFloat(v) || 0;
const fmt = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

export default function ITRFiling() {
  const [clients, setClients] = useState([]);
  const [filings, setFilings] = useState([]);
  const [meta, setMeta] = useState({ client_id: "", assessment_year: "2026-27", itr_type: "", regime: "new", agricultural_income: 0, foreign_income_or_assets: false });
  const [d, setD] = useState({});
  const [openSec, setOpenSec] = useState({ salary: true });
  const [detected, setDetected] = useState(null);
  const [taxCalc, setTaxCalc] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [autoFilled, setAutoFilled] = useState(new Set());
  const [fileModal, setFileModal] = useState(null);
  const [fileCalc, setFileCalc] = useState(null);
  const [fileRegime, setFileRegime] = useState("new");
  const [efileJob, setEfileJob] = useState(null);
  const [efileErr, setEfileErr] = useState("");
  const [efiling, setEfiling] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const prevJsonRef = useRef(null);

  const touch = (f) => setAutoFilled((s) => { if (!s.has(f)) return s; const ns = new Set(s); ns.delete(f); return ns; });

  const client = clients.find((c) => c.id === meta.client_id);
  const ctype = client?.client_type || "individual";

  useEffect(() => {
    axios.get(`${API}/it/clients`).then((r) => setClients(r.data)).catch(() => {});
    fetchFilings();
  }, []);
  const fetchFilings = async () => { try { setFilings((await axios.get(`${API}/it/filings`)).data); } catch (e) { /* noop */ } };

  /* -------- derived totals (integrated computation) -------- */
  const T = useMemo(() => {
    const stdDed = meta.regime === "new" ? 75000 : 50000;
    const salGross = n(d.sal_171) + n(d.sal_172) + n(d.sal_173);
    const salary = Math.max(salGross - n(d.sal_exempt) - (salGross > 0 ? stdDed : 0) - n(d.sal_prof_tax) - n(d.sal_ent_allow), 0);
    let hp = 0;
    if (d.hp_type === "self_occupied") hp = Math.max(-n(d.hp_interest), -200000);
    else hp = Math.round((n(d.hp_gross_rent) - n(d.hp_municipal)) * 0.7 + n(d.hp_arrears) - n(d.hp_interest));
    const business = n(d.biz_net_profit) + n(d.biz_44ad) + n(d.biz_44ada) + n(d.biz_44ae) + n(d.biz_partner_remuneration);
    const cg = Math.max(n(d.cg_stcg_111a) + n(d.cg_stcg_other) + n(d.cg_ltcg_112a) + n(d.cg_ltcg_other) - n(d.cg_exemption_54), 0);
    const os = n(d.os_savings_interest) + n(d.os_fd_interest) + n(d.os_dividend) + n(d.os_family_pension) + n(d.os_lottery) + n(d.os_other);
    const trust = n(d.tr_voluntary) - n(d.tr_applied) - n(d.tr_corpus);
    const via = ["d_80c", "d_80ccc", "d_80ccd1b", "d_80ccd2", "d_80d", "d_80dd", "d_80ddb", "d_80e", "d_80eea", "d_80g", "d_80gg", "d_80tta", "d_80ttb", "d_80u", "d_other"].reduce((s, k) => s + n(d[k]), 0);
    const advance = n(d.tp_adv_q1) + n(d.tp_adv_q2) + n(d.tp_adv_q3) + n(d.tp_adv_q4);
    const paid = n(d.tp_tds_salary) + n(d.tp_tds_other) + n(d.tp_tcs) + advance + n(d.tp_self_assessment);
    const gti = salary + hp + business + cg + os + (ctype === "trust" ? Math.max(trust, 0) : 0);
    return { salary, hp, business, cg, os, via, advance, paid, gti, stdDed };
  }, [d, meta.regime, ctype]);

  const summary = useMemo(() => ({
    client_id: meta.client_id, assessment_year: meta.assessment_year, itr_type: meta.itr_type || undefined,
    regime: meta.regime, salary_income: T.salary, house_property_count: parseInt(d.hp_count) || 0,
    house_property_income: T.hp, business_income: T.business,
    presumptive_business: n(d.biz_44ad) + n(d.biz_44ada) + n(d.biz_44ae) > 0 && !n(d.biz_net_profit),
    capital_gains: T.cg, other_sources_income: T.os, agricultural_income: n(meta.agricultural_income),
    foreign_income_or_assets: !!meta.foreign_income_or_assets, deductions_80c: n(d.d_80c), deductions_80d: n(d.d_80d),
    other_deductions: T.via - n(d.d_80c) - n(d.d_80d), tds_total: n(d.tp_tds_salary) + n(d.tp_tds_other),
    advance_tax_total: T.advance, self_assessment_tax: n(d.tp_self_assessment),
    employer_name: d.employer_name || "", status: "draft", notes: d.notes || "", details: d,
  }), [meta, d, T]);

  /* -------- live ITR detection + tax computation -------- */
  useEffect(() => {
    if (!meta.client_id) { setDetected(null); setTaxCalc(null); return; }
    const t = setTimeout(async () => {
      try {
        const [det, tax] = await Promise.all([
          axios.post(`${API}/it/detect-itr`, summary),
          axios.post(`${API}/it/compute-tax`, {
            client_type: ctype, gross_total_income: T.gti, total_deductions: T.via,
            stcg_111a: n(d.cg_stcg_111a), ltcg_112a: n(d.cg_ltcg_112a), taxes_paid: T.paid,
          }),
        ]);
        setDetected(det.data); setTaxCalc(tax.data);
      } catch (e) { /* noop */ }
    }, 500);
    return () => clearTimeout(t);
  }, [summary]); // eslint-disable-line react-hooks/exhaustive-deps

  const extractDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setExtracting(true); setError(""); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = (await axios.post(`${API}/it/filings/extract`, fd, { timeout: 180000 })).data;
      if (r.fields && Object.keys(r.fields).length) {
        const keys = Object.keys(r.fields);
        setD((p) => ({ ...p, ...r.fields }));
        setAutoFilled(new Set(keys));
        const secOpen = {};
        Object.entries(SCHEDULES).forEach(([id, s]) => {
          const all = [...(s.fields || []), ...(s.text || []), ...(s.select || [])].map((x) => x[0]);
          if (all.some((k) => keys.includes(k))) secOpen[id] = true;
        });
        setOpenSec((o) => ({ ...o, ...secOpen }));
        if (!meta.client_id && r.pan) {
          const m = clients.find((c) => c.pan === r.pan);
          if (m) setMeta((mm) => ({ ...mm, client_id: m.id }));
        }
        setMsg(`✓ Last year JSON: ${keys.length} fields auto-filled — review and edit the highlighted (amber) values`);
        return;
      }
      setD((p) => ({
        ...p,
        sal_171: r.salary_income || p.sal_171, employer_name: r.employer_name || p.employer_name,
        d_80c: r.deductions_80c || p.d_80c, d_80d: r.deductions_80d || p.d_80d, d_other: r.other_deductions || p.d_other,
        tp_tds_salary: r.tds_total || p.tp_tds_salary,
        os_savings_interest: r.other_sources_income || p.os_savings_interest,
        hp_gross_rent: r.house_property_income ? Math.round(r.house_property_income / 0.7) : p.hp_gross_rent,
      }));
      setMsg(`✓ ${r.source === "json" ? "JSON" : "Form-16/PDF"} : data auto-filled${r.employer_name ? ` (${r.employer_name})` : ""}`);
    } catch (err) { setError(err.response?.data?.detail || "Extraction failed"); }
    finally { setExtracting(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!meta.client_id) { setError("Select a client first"); return null; }
    setBusy(true); setError("");
    try {
      const payload = { ...summary, itr_type: meta.itr_type || detected?.itr };
      let saved;
      if (editingId) {
        saved = (await axios.put(`${API}/it/filings/${editingId}`, payload)).data;
        setMsg("✓ Filing updated");
      } else {
        saved = (await axios.post(`${API}/it/filings`, payload)).data;
        setEditingId(saved.id);
        setMsg("✓ ITR filing saved");
      }
      fetchFilings();
      return saved;
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); return null; }
    finally { setBusy(false); }
  };

  /* -------- final step: regime choose + e-file -------- */
  const openFileModal = async (f) => {
    setEfileJob(null); setEfileErr(""); setFileCalc(null);
    setFileModal(f);
    setFileRegime(f.regime || "new");
    const det = f.details || {};
    try {
      const r = await axios.post(`${API}/it/compute-tax`, {
        client_type: clients.find((c) => c.id === f.client_id)?.client_type || "individual",
        gross_total_income: (f.salary_income || 0) + (f.house_property_income || 0) + (f.business_income || 0) + (f.capital_gains || 0) + (f.other_sources_income || 0),
        total_deductions: (f.deductions_80c || 0) + (f.deductions_80d || 0) + (f.other_deductions || 0),
        stcg_111a: n(det.cg_stcg_111a), ltcg_112a: n(det.cg_ltcg_112a),
        taxes_paid: (f.tds_total || 0) + (f.advance_tax_total || 0) + (f.self_assessment_tax || 0),
      });
      setFileCalc(r.data);
      if (r.data.recommended_regime && !f.regime) setFileRegime(r.data.recommended_regime);
    } catch (e) { /* noop */ }
    try { setEfileJob((await axios.get(`${API}/it/filings/${f.id}/efile-status`)).data); } catch (e) { /* no job yet */ }
  };

  const saveAndFile = async () => {
    const saved = await save();
    if (saved) openFileModal(saved);
  };

  const closeFileModal = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setFileModal(null); setEfileJob(null); setEfileErr("");
  };

  const downloadItrJson = async (f) => {
    try {
      const r = await axios.get(`${API}/it/filings/${f.id}/itr-json`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(f.itr_type || "ITR").replace("-", "")}_${f.client_pan}_${f.assessment_year}.json`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setEfileErr("ITR JSON download failed"); }
  };

  const startAutoFile = async () => {
    if (!fileModal) return;
    setEfiling(true); setEfileErr("");
    try {
      const job = (await axios.post(`${API}/it/filings/${fileModal.id}/auto-file`, { regime: fileRegime })).data;
      setEfileJob(job);
      pollRef.current = setInterval(async () => {
        try {
          const j = (await axios.get(`${API}/it/filings/${fileModal.id}/efile-status`)).data;
          setEfileJob(j);
          if (j.status === "filed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setEfiling(false); fetchFilings();
          }
        } catch (e) { /* noop */ }
      }, 1300);
    } catch (e) {
      setEfileErr(e.response?.data?.detail || "Auto-filing could not start");
      setEfiling(false);
    }
  };

  const loadFiling = (f) => {
    setEditingId(f.id);
    setMeta({ client_id: f.client_id, assessment_year: f.assessment_year, itr_type: f.itr_type || "",
      regime: f.regime || "new", agricultural_income: f.agricultural_income || 0,
      foreign_income_or_assets: !!f.foreign_income_or_assets });
    setD(f.details || {});
    setMsg(`✎ Editing filing: ${f.client_name} — AY ${f.assessment_year}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const downloadPdf = async (f) => {
    try {
      const r = await axios.get(`${API}/it/filings/${f.id}/computation-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Computation_${f.client_pan}_${f.assessment_year}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError("PDF download failed"); }
  };

  const removeFiling = async (fid) => {
    if (!window.confirm("Delete this filing?")) return;
    await axios.delete(`${API}/it/filings/${fid}`); fetchFilings();
  };

  const visibleSchedules = Object.entries(SCHEDULES).filter(([k, s]) => !s.entity || s.entity.includes(ctype));

  const Section = ({ id, s }) => (
    <div className="border border-slate-200 rounded-sm bg-white">
      <button type="button" onClick={() => setOpenSec((o) => ({ ...o, [id]: !o[id] }))} data-testid={`sec-toggle-${id}`}
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <CaretDown size={14} className={`transition-transform ${openSec[id] ? "" : "-rotate-90"}`} />
        <span className="font-bold text-sm text-slate-900">{s.title}</span>
        <span className="ml-auto text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm">{s.itr}</span>
      </button>
      {openSec[id] && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(s.text || []).map(([f, label]) => (
              <div key={f} className="col-span-2"><Label className="text-[11px]">{label}</Label>
                <Input data-testid={`f-${f}`} value={d[f] || ""} className={autoFilled.has(f) ? "border-amber-400 bg-amber-50" : ""}
                  onChange={(e) => { touch(f); setD({ ...d, [f]: e.target.value }); }} /></div>
            ))}
            {(s.select || []).map(([f, label, opts]) => (
              <div key={f}><Label className="text-[11px]">{label}</Label>
                <select data-testid={`f-${f}`} value={d[f] || opts[0]} onChange={(e) => { touch(f); setD({ ...d, [f]: e.target.value }); }}
                  className={`w-full px-2 py-2 border rounded-sm text-sm ${autoFilled.has(f) ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
                  {opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                </select></div>
            ))}
            {s.fields.map(([f, label, isInt]) => (
              <div key={f}><Label className="text-[11px]">{label}</Label>
                <Input type="number" data-testid={`f-${f}`} value={d[f] ?? ""} placeholder="0" className={autoFilled.has(f) ? "border-amber-400 bg-amber-50" : ""}
                  onChange={(e) => { touch(f); setD({ ...d, [f]: isInt ? (parseInt(e.target.value) || "") : e.target.value }); }} /></div>
            ))}
          </div>
          {(s.checks || []).length > 0 && (
            <div className="flex gap-5 mt-3">
              {s.checks.map(([f, label]) => (
                <label key={f} className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid={`f-${f}`} checked={!!d[f]} onChange={(e) => setD({ ...d, [f]: e.target.checked })} className="accent-emerald-800" /> {label}</label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 sm:p-8" data-testid="itr-filing-page">
      <div className="flex items-center gap-3 mb-5">
        <FileText size={30} weight="duotone" className="text-emerald-800" />
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Integrated ITR Form (ITR-1 to ITR-7)</h1>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-3">
          {/* Part A — General */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className="text-[11px]">Client *</Label>
              <select data-testid="itr-client-select" value={meta.client_id} onChange={(e) => setMeta({ ...meta, client_id: e.target.value })} className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="">— Select client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.pan}) · {c.client_type}</option>)}
              </select>
            </div>
            <div><Label className="text-[11px]">Assessment Year</Label>
              <select data-testid="itr-ay" value={meta.assessment_year} onChange={(e) => setMeta({ ...meta, assessment_year: e.target.value })} className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm">{AY.map((a) => <option key={a}>{a}</option>)}</select></div>
            <div><Label className="text-[11px]">ITR Type</Label>
              <select data-testid="itr-type" value={meta.itr_type} onChange={(e) => setMeta({ ...meta, itr_type: e.target.value })} className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="">Auto-detect</option>{ITR_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><Label className="text-[11px]">Tax Regime</Label>
              <select data-testid="itr-regime" value={meta.regime} onChange={(e) => setMeta({ ...meta, regime: e.target.value })} className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm">
                <option value="new">New (115BAC)</option><option value="old">Old</option></select></div>
            <div><Label className="text-[11px]">Agricultural Income</Label>
              <Input type="number" data-testid="itr-agri" value={meta.agricultural_income} onChange={(e) => setMeta({ ...meta, agricultural_income: e.target.value })} /></div>
            <label className="flex items-end gap-2 text-xs pb-2 col-span-2"><input type="checkbox" data-testid="itr-foreign" checked={meta.foreign_income_or_assets} onChange={(e) => setMeta({ ...meta, foreign_income_or_assets: e.target.checked })} className="accent-emerald-800" /> Foreign income / assets (Schedule FA)</label>
          </div>

          {/* Auto-fill uploader */}
          <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/50 rounded-sm p-3 flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".json,.pdf" className="hidden" onChange={extractDoc} data-testid="itr-doc-input" />
            <input ref={prevJsonRef} type="file" accept=".json" className="hidden" onChange={extractDoc} data-testid="itr-prev-json-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={extracting} data-testid="itr-upload-btn" className="border-emerald-700 text-emerald-800 hover:bg-emerald-100">
              <UploadSimple size={15} className="mr-1" /> {extracting ? "AI Reading…" : "Upload Form-16 / JSON / PDF"}
            </Button>
            <Button size="sm" onClick={() => prevJsonRef.current?.click()} disabled={extracting} data-testid="itr-prev-json-btn" className="bg-amber-500 hover:bg-amber-600 text-white">
              <FileText size={15} className="mr-1" /> {extracting ? "Reading…" : "Import Last Year JSON"}
            </Button>
            <p className="text-[11px] text-emerald-900/70">Saare schedules (Salary, HP, CG, OS, 80C…) will be <b>auto-filled</b> — then review/edit.</p>
          </div>

          {/* All schedules */}
          {visibleSchedules.map(([k, s]) => <Section key={k} id={k} s={s} />)}

          {msg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-3 rounded-sm" data-testid="itr-msg">{msg}</div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="itr-error">{error}</div>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy || !meta.client_id} data-testid="itr-save-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white">
              {busy ? "Saving…" : editingId ? "Update Filing" : "Save ITR Filing"}
            </Button>
            <Button onClick={saveAndFile} disabled={busy || !meta.client_id} data-testid="itr-save-file-btn" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Lightning size={15} weight="fill" className="mr-1" /> Save & Proceed to File
            </Button>
            {editingId && (
              <Button variant="outline" onClick={() => { setEditingId(null); setD({}); setMsg(""); }} data-testid="itr-cancel-edit-btn">
                Cancel Edit
              </Button>
            )}
          </div>
        </div>

        {/* ===== Right rail ===== */}
        <div className="space-y-4">
          <div className="bg-emerald-900 text-white rounded-sm p-5" data-testid="itr-detect-card">
            <div className="flex items-center gap-2 mb-1"><Sparkle size={16} weight="fill" className="text-amber-300" /><p className="text-[10px] font-black uppercase tracking-widest text-amber-300">Auto-Detected ITR</p></div>
            {detected ? (<><p className="text-3xl font-black tracking-tighter" data-testid="itr-detected-type">{detected.itr}</p><p className="text-[11px] text-emerald-100/80 mt-1">{detected.reason}</p></>)
              : <p className="text-xs text-emerald-100/70">Client select karke income bharo.</p>}
          </div>

          {/* Income summary */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 text-xs space-y-1.5" data-testid="itr-summary-card">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Income Summary (auto)</p>
            {[["Salary (net of std ded.)", T.salary], ["House Property", T.hp], ["Business / Profession", T.business],
              ["Capital Gains", T.cg], ["Other Sources", T.os]].map(([l, v]) => (
              <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-mono font-bold">{fmt(v)}</span></div>
            ))}
            <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="font-bold">Gross Total Income</span><span className="font-mono font-black" data-testid="itr-gti">{fmt(T.gti)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Chapter VI-A Deductions</span><span className="font-mono font-bold text-red-600">− {fmt(T.via)}</span></div>
          </div>

          {/* Tax computation */}
          {taxCalc && (
            <div className="bg-white border-2 border-emerald-800 rounded-sm p-4 text-xs" data-testid="itr-tax-card">
              <div className="flex items-center gap-2 mb-3"><Scales size={16} weight="duotone" className="text-emerald-800" /><p className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Tax Computation (AY {meta.assessment_year})</p></div>
              {taxCalc.new_regime ? (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {[["NEW Regime", taxCalc.new_regime], ["OLD Regime", taxCalc.old_regime]].map(([l, r]) => (
                      <div key={l} className={`border rounded-sm p-2 ${taxCalc.recommended_regime === l.split(" ")[0].toLowerCase() ? "border-emerald-700 bg-emerald-50" : "border-slate-200"}`}>
                        <p className="font-black">{l}{taxCalc.recommended_regime === l.split(" ")[0].toLowerCase() && " ★"}</p>
                        <p className="text-slate-500">Taxable: <b className="text-slate-900">{fmt(r.taxable_income)}</b></p>
                        <p className="text-slate-500">Tax+Cess: <b className="text-slate-900">{fmt(r.total_tax_liability)}</b></p>
                      </div>
                    ))}
                  </div>
                  <p className="text-emerald-800 font-bold" data-testid="itr-regime-reco">★ {taxCalc.recommended_regime.toUpperCase()} regime better — bachao {fmt(taxCalc.savings)}</p>
                  {(() => { const r = taxCalc[`${meta.regime}_regime`]; return (
                    <div className="border-t border-slate-200 mt-2 pt-2 space-y-1">
                      <div className="flex justify-between"><span>Rebate 87A</span><span className="font-mono">− {fmt(r.rebate_87a)}</span></div>
                      <div className="flex justify-between"><span>Surcharge + Cess</span><span className="font-mono">{fmt(r.surcharge + r.cess)}</span></div>
                      <div className="flex justify-between"><span>Taxes Paid (TDS+Advance+SAT)</span><span className="font-mono">− {fmt(r.taxes_paid)}</span></div>
                      <div className={`flex justify-between font-black text-sm border-t pt-1 ${r.payable > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="itr-payable">
                        <span>{r.payable > 0 ? "TAX PAYABLE" : "REFUND DUE"}</span><span className="font-mono">{fmt(r.payable > 0 ? r.payable : r.refund)}</span>
                      </div>
                    </div>); })()}
                </>
              ) : (
                <div className="space-y-1">
                  <p className="font-bold">Entity Rate: {taxCalc.entity_rate}</p>
                  <div className="flex justify-between"><span>Total Tax Liability</span><span className="font-mono font-bold">{fmt(taxCalc.computation.total_tax_liability)}</span></div>
                  <div className={`flex justify-between font-black ${taxCalc.computation.payable > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="itr-payable">
                    <span>{taxCalc.computation.payable > 0 ? "TAX PAYABLE" : "REFUND DUE"}</span>
                    <span className="font-mono">{fmt(taxCalc.computation.payable > 0 ? taxCalc.computation.payable : taxCalc.computation.refund)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Saved filings */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <p className="px-4 py-3 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">Saved Filings ({filings.length})</p>
            <div className="max-h-[300px] overflow-y-auto">
              {filings.length === 0 && <p className="p-4 text-xs text-slate-400">No filings saved yet.</p>}
              {filings.map((f) => (
                <div key={f.id} className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2" data-testid={`itr-filing-${f.id}`}>
                  <button onClick={() => loadFiling(f)} className="flex-1 text-left hover:opacity-70" data-testid={`itr-edit-${f.id}`} title="Click to edit">
                    <p className="text-xs font-bold text-slate-900">{f.client_name} <span className="text-[9px] font-mono text-slate-400">{f.client_pan}</span></p>
                    <p className="text-[10px] text-slate-500">AY {f.assessment_year} · <b className="text-emerald-800">{f.itr_type}</b> · {f.status}{f.ack_no ? <span className="text-emerald-700"> · Ack {f.ack_no}</span> : ""}</p>
                  </button>
                  <button onClick={() => openFileModal(f)} className="text-amber-500 hover:text-amber-700" data-testid={`itr-efile-${f.id}`} title="File ITR (final step)"><Lightning size={16} weight="fill" /></button>
                  <button onClick={() => downloadPdf(f)} className="text-emerald-700 hover:text-emerald-900" data-testid={`itr-pdf-${f.id}`} title="Computation PDF"><FilePdf size={16} /></button>
                  <button onClick={() => removeFiling(f.id)} className="text-red-300 hover:text-red-600" data-testid={`itr-del-${f.id}`}><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== FINAL STEP MODAL — Regime choose + Auto e-File ===== */}
      {fileModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="itr-file-modal">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <p className="text-sm font-black text-slate-900">Final Step — File ITR</p>
                <p className="text-[11px] text-slate-500">{fileModal.client_name} ({fileModal.client_pan}) · AY {fileModal.assessment_year} · {fileModal.itr_type}</p>
              </div>
              <button onClick={closeFileModal} data-testid="itr-file-modal-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Regime chooser */}
              {fileCalc?.new_regime ? (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Select Tax Regime *</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[["new", "NEW Regime (115BAC)", fileCalc.new_regime], ["old", "OLD Regime", fileCalc.old_regime]].map(([key, label, r]) => (
                      <button key={key} type="button" onClick={() => setFileRegime(key)} data-testid={`regime-pick-${key}`} disabled={!!efileJob && efileJob.status !== "filed"}
                        className={`text-left border-2 rounded-sm p-3 transition-colors ${fileRegime === key ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-black">{label}</p>
                          <span className={`w-3.5 h-3.5 rounded-full border-2 ${fileRegime === key ? "border-emerald-700 bg-emerald-700" : "border-slate-300"}`} />
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">Taxable: <b className="text-slate-900">{fmt(r.taxable_income)}</b></p>
                        <p className="text-[11px] text-slate-500">Tax Liability: <b className="text-slate-900">{fmt(r.total_tax_liability)}</b></p>
                        {fileCalc.recommended_regime === key && <p className="text-[10px] font-bold text-emerald-700 mt-1">★ Recommended — {fmt(fileCalc.savings)} bachat</p>}
                      </button>
                    ))}
                  </div>
                  {(() => { const r = fileCalc[`${fileRegime}_regime`]; return (
                    <div className={`mt-3 flex justify-between items-center border rounded-sm px-3 py-2 text-sm font-black ${r.payable > 0 ? "bg-amber-50 border-amber-300 text-red-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`} data-testid="itr-file-payable">
                      <span>{r.payable > 0 ? "TAX PAYABLE" : "REFUND DUE"}</span>
                      <span className="font-mono">{fmt(r.payable > 0 ? r.payable : r.refund)}</span>
                    </div>); })()}
                </div>
              ) : fileCalc?.computation ? (
                <div className="border border-slate-200 rounded-sm p-3 text-xs">
                  <p className="font-bold mb-1">Entity Rate: {fileCalc.entity_rate}</p>
                  <div className="flex justify-between font-black"><span>Total Tax Liability</span><span className="font-mono">{fmt(fileCalc.computation.total_tax_liability)}</span></div>
                </div>
              ) : <p className="text-xs text-slate-400">Computation load ho raha hai…</p>}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadItrJson(fileModal)} data-testid="itr-json-download-btn" className="border-emerald-700 text-emerald-800 hover:bg-emerald-50">
                  <DownloadSimple size={15} className="mr-1" /> Download ITR JSON
                </Button>
                <Button size="sm" onClick={startAutoFile} disabled={efiling || (efileJob && efileJob.status === "filed")} data-testid="itr-autofile-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white">
                  <Lightning size={15} weight="fill" className="mr-1" />
                  {efileJob?.status === "filed" ? "Filed ✓" : efiling ? "Filing…" : "Auto-File on IT Portal"}
                </Button>
              </div>
              {efileErr && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm" data-testid="itr-efile-error">{efileErr}</div>}

              {/* Live status stepper */}
              {efileJob && (
                <div className="border border-slate-200 rounded-sm p-4" data-testid="itr-efile-stepper">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">e-Filing Status</p>
                  <div className="space-y-2.5">
                    {(efileJob.steps || []).map((lbl, i) => {
                      const done = i < efileJob.current_step || efileJob.status === "filed";
                      const current = i === efileJob.current_step && efileJob.status !== "filed";
                      return (
                        <div key={lbl} className="flex items-center gap-2.5 text-xs">
                          {done ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />
                            : current ? <CircleNotch size={18} className="text-amber-500 animate-spin shrink-0" />
                            : <span className="w-[18px] h-[18px] rounded-full border-2 border-slate-200 shrink-0" />}
                          <span className={done ? "text-slate-900 font-bold" : current ? "text-amber-600 font-bold" : "text-slate-400"}>{lbl}</span>
                        </div>
                      );
                    })}
                  </div>
                  {efileJob.status === "filed" && (
                    <div className="mt-3 bg-emerald-50 border border-emerald-300 rounded-sm p-3" data-testid="itr-efile-ack">
                      <p className="text-xs font-black text-emerald-800">🎉 ITR Filed Successfully!</p>
                      <p className="text-[11px] text-emerald-700 mt-0.5">Acknowledgment No: <b className="font-mono">{efileJob.ack_no}</b> · Filed on {efileJob.filed_on}</p>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-3">⚠ Portal login/submission is simulated (the official e-filing API is available only to licensed ERIs). The generated ITR JSON is 100% real — you can also upload it manually on the portal.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
